/* eslint-disable react-hooks/exhaustive-deps */
import { FunctionComponent, useEffect, useState } from 'react';
import './UserAdminList.css';
import { Headline, Gutter, Div, Button, Flex } from '@gaiads/telia-react-component-library';
import { useDispatch } from 'react-redux';
import {
  CustomPaginator,
  GenericTable,
  GenericTableProps,
  InviteUserModal,
  InviteUserModalTranslations,
  InviteUserPayload,
  SearchAndFilter,
  SearchAndFilterOnChange,
  SearchWithoutScopeProps,
} from '@cdaas/reusable-components-daas';
import {
  ConflictErrorErrorEnum,
  InvoicingAccountOptionResponseContents,
  UserOrganizationWithUserGroupsResponse,
  UserPoliciesRequest,
  UserPoliciesResponse,
  UserWithUserGroupAndOrganizationList,
  UserWithUserGroupAndOrganizationResponse,
} from '@cdaas/api-clients/dist/asset-user-openapi';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { createUser, getQueryParams, getOrganization, getUsersPaged, updateQueryParams, getInvoicingAccounts } from '../../../utils';
import { COUNTRY_CALLING_CODES, DEFAULT_PAGE_SIZE } from '../../../utils/constants';
import { showGrowlNotification } from '../../../store/actions';

export const UserSearchColumnEnum = {
  Name: 'name',
  UserGroup: 'user_group',
  Email: 'email',
} as const;

export const UserSearchSortEnum = {
  ...UserSearchColumnEnum,
  NameMinus: '-name',
  UserGroupMinus: '-user_group',
  EmailMinus: '-email',
} as const;

const pageBasePath = '/users';

export type UserSearchSort = (typeof UserSearchSortEnum)[keyof typeof UserSearchSortEnum];
export type UserSearchColumn = (typeof UserSearchColumnEnum)[keyof typeof UserSearchColumnEnum];

const UserAdminList: FunctionComponent = () => {
  const dispatch = useDispatch();

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingInvoicingAccounts, setLoadingInvoicingAccounts] = useState(true);
  const history = useHistory();

  const [searchText, setSearchText] = useState<string>();
  const [sort, setSort] = useState<UserSearchSort>(UserSearchSortEnum.Name);

  const [invoicingAccounts, setInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>();
  const [pagedUsers, setPagedUsers] = useState<UserWithUserGroupAndOrganizationList>();

  const [usersFetchError, setUsersFetchError] = useState(false);

  const [organization, setOrganization] = useState<UserOrganizationWithUserGroupsResponse | null>(null);
  const [showInviteUserModal, setShowInviteUserModal] = useState<boolean>(false);

  const { t } = useTranslation('editOrganization');
  const { t: tGlobal } = useTranslation('globalComponents');
  const { t: tErrors } = useTranslation('apiErrors');

  const fetchOrganization = async () => {
    setOrganization(await getOrganization('user_groups'));
  };

  const fetchInvoicingAccounts = async () => {
    setLoadingInvoicingAccounts(true);
    const res = await getInvoicingAccounts();
    if (res) {
      setInvoicingAccounts(res);
    } else {
      dispatch(showGrowlNotification(tErrors('invoicingAccount.getAllInvoicingAccountsError'), 'error'));
    }
    setLoadingInvoicingAccounts(false);
  };

  const fetchPagedUsers = async (search?: string, sort?: UserSearchSort, page?: number) => {
    setLoadingUsers(true);
    setUsersFetchError(false);
    const res = await getUsersPaged(search, sort, page, DEFAULT_PAGE_SIZE);
    if (res) {
      setPagedUsers(res);
    } else {
      setUsersFetchError(true);
    }
    setLoadingUsers(false);
  };

  useEffect(() => {
    const params = getQueryParams(new URLSearchParams(history.location.search), [
      {
        name: 'search',
        type: 'string',
        defaultValue: '',
      },
      {
        name: 'sort',
        type: 'string',
        defaultValue: 'name',
        allowedValues: Object.values(UserSearchSortEnum),
      },
      {
        name: 'page',
        type: 'number',
        defaultValue: 0,
      },
    ]);
    setSearchText(params.search as string);
    fetchPagedUsers(params.search as string, params.sort as UserSearchSort, +params.page);
  }, [history.location.search]);

  useEffect(() => {
    fetchOrganization();
    fetchInvoicingAccounts();
  }, []);

  const translations: InviteUserModalTranslations = {
    modalTitle: t('usersAdmin.inviteUser.modalTitle'),
    emailInputLabel: t('usersAdmin.inviteUser.emailInputLabel'),
    fieldRequired: t('usersAdmin.inviteUser.fieldRequired'),
    saveButton: t('usersAdmin.inviteUser.saveButton'),
    closeButton: t('usersAdmin.inviteUser.closeButton'),
    saveApiErrorTitle: t('usersAdmin.inviteUser.saveApiErrorTitle'),
    saveApiErrorMessage: t('usersAdmin.inviteUser.saveApiErrorMessage'),
    assignToUserGroup: t('usersAdmin.inviteUser.assignToUserGroup'),
    required: t('usersAdmin.inviteUser.required'),
    invalidEmail: t('usersAdmin.inviteUser.invalidEmail'),
    invalidPhoneNumber: t('usersAdmin.inviteUser.invalidPhoneNumber'),
    phoneNumberInputLabel: t('usersAdmin.inviteUser.phoneNumberInputLabel'),
    purchaseLimitsTitle: t('usersAdmin.inviteUser.purchaseLimitsTitle'),
    purchaseLimitsDescription: t('usersAdmin.inviteUser.purchaseLimitsDescription'),
    limitVisibilityTitle: t('usersAdmin.inviteUser.limitVisibilityTitle'),
    overrideVisibilityCheckboxLabel: t('usersAdmin.inviteUser.overrideVisibilityCheckboxLabel'),
    maxPricePerProductInputLabel: t('usersAdmin.inviteUser.maxPricePerProductInputLabel', {
      currency: organization?.locale.currency.toUpperCase() ?? '',
    }),
    noVisibilityRestrictionMessage: t('usersAdmin.inviteUser.noVisibilityRestrictionMessage'),
    restrictedVisibilityMessage: t('usersAdmin.inviteUser.restrictedVisibilityMessage'),
    restrictedVisibilityMessageEnding: t('usersAdmin.inviteUser.restrictedVisibilityMessageEnding'),
    month: t('usersAdmin.inviteUser.month'),
    subscriptionLimitsTitle: t('usersAdmin.inviteUser.subscriptionLimitsTitle'),
    limitSubscriptionCheckBoxLabel: t('usersAdmin.inviteUser.limitSubscriptionCheckBoxLabel'),
    enabledSingleDevicePolicyMessage: t('usersAdmin.inviteUser.enabledSingleDevicePolicyMessage'),
    disabledSingleDevicePolicyMessage: t('usersAdmin.inviteUser.disabledSingleDevicePolicyMessage'),
    procurementLimitTypeError: t('usersAdmin.inviteUser.procurementLimitTypeError'),
    invoicingAccount: {
      clearSelection: t('usersAdmin.inviteUser.invoicingAccount.clearSelection'),
      dropdownLabel: t('usersAdmin.inviteUser.invoicingAccount.dropdownLabel'),
      title: t('usersAdmin.inviteUser.invoicingAccount.title'),
      userGroupNotSelected: t('usersAdmin.inviteUser.invoicingAccount.userGroupNotSelected'),
      noOptionsFoundInfo: t('usersAdmin.inviteUser.invoicingAccount.noOptionsFoundInfo'),
    },
    showExpanderButton: t('usersAdmin.inviteUser.showExpanderButton'),
    hideExpanderButton: t('usersAdmin.inviteUser.hideExpanderButton'),
    employeeIdInputLabel: t('usersAdmin.inviteUser.employeeIdInputLabel'),
    countries: {
      Finland: tGlobal('countries.Finland'),
      Sweden: tGlobal('countries.Sweden'),
      Norway: tGlobal('countries.Norway'),
      Denmark: tGlobal('countries.Denmark'),
      UnitedKingdom: tGlobal('countries.UnitedKingdom'),
    },
  };

  const inviteNewUser = async (userPayload: InviteUserPayload): Promise<boolean> => {
    const policies: UserPoliciesRequest | undefined =
      userPayload.user.policies?.lease_procurement_limit !== null && userPayload.user.policies?.lease_procurement_limit !== undefined
        ? {
            lease_procurement_limit: userPayload.user.policies?.lease_procurement_limit,
          }
        : undefined;
    const newUser = await createUser(
      {
        email: userPayload.user.email,
        phone_number: userPayload.user.phone_number,
        employee_id: userPayload.user.employee_id,
        allow_access_without_bank_id: false,
        policies: policies as UserPoliciesResponse,
        invoicing_account: userPayload.user.invoicing_account,
        user_roles: [],
        allowed_procurements: [],
      },
      userPayload.selectedUserGroupId!,
    );
    if (isAxiosError(newUser) || newUser === null) {
      switch (newUser?.response?.data?.error) {
        case ConflictErrorErrorEnum.EmailAlreadyInUse:
          translations.saveApiErrorMessage = tErrors('user.saveApiErrorMessageEmailInUse');
          break;
        case ConflictErrorErrorEnum.PhoneNumberAlreadyInUse:
          translations.saveApiErrorMessage = tErrors('user.saveApiErrorMessagePhoneNumberInUse');
          break;
        default:
          translations.saveApiErrorMessage = t('usersAdmin.inviteUser.saveApiErrorMessage');
          break;
      }
      return false;
    } else {
      fetchPagedUsers(searchText, sort, 0);
      setShowInviteUserModal(false);
      return true;
    }
  };

  const getNewSortValue = (currentSort: UserSearchSort, column: UserSearchColumn): UserSearchSort => {
    return !currentSort.includes(column) ? column : currentSort.startsWith('-') ? column : (`-${column}` as UserSearchSort);
  };

  const buttonConfig: GenericTableProps<UserWithUserGroupAndOrganizationResponse>['buttonConfig'] = {
    name: t('usersAdmin.usersAdminList.genericTable.selectButton'),
    buttonClickCallback: (user: UserWithUserGroupAndOrganizationResponse) =>
      history.push(`users/usergroup/${user.user_group?.id ?? 'no-id'}/user/${user.id!}`),
  };

  const rowConfigs: GenericTableProps<UserWithUserGroupAndOrganizationResponse>['rowConfigs'] = [
    {
      header: t('usersAdmin.usersAdminList.genericTable.headerName'),
      key: 'name',
      // If user has no name (i.e. user has never logged in so is considered inactive), don't show empty name but inactive user text
      formatValue: (name: string) => name || <span style={{ fontStyle: 'italic' }}>{t('usersAdmin.usersAdminList.inactiveUser')}</span>,
      fullyExternalSortFunction: () => {
        setSort(prevState => {
          const newSort = getNewSortValue(prevState, UserSearchColumnEnum.Name);
          updateQueryParams(pageBasePath, { search: searchText!, sort: newSort, page: 0 }, 'replace');
          return newSort;
        });
      },
    },
    {
      header: t('usersAdmin.usersAdminList.genericTable.headerUserGroup'),
      key: 'user_group.name',
      fullyExternalSortFunction: () => {
        setSort(prevState => {
          const newSort = getNewSortValue(prevState, UserSearchColumnEnum.UserGroup);
          updateQueryParams(pageBasePath, { search: searchText!, sort: newSort, page: 0 }, 'replace');
          return newSort;
        });
      },
    },
    {
      header: t('usersAdmin.usersAdminList.genericTable.headerEmail'),
      key: 'email',
      fullyExternalSortFunction: () => {
        setSort(prevState => {
          const newSort = getNewSortValue(prevState, UserSearchColumnEnum.Email);
          updateQueryParams(pageBasePath, { search: searchText!, sort: newSort, page: 0 }, 'replace');
          return newSort;
        });
      },
    },
  ];

  const handleSearchChange = (values: SearchAndFilterOnChange) => {
    const newSearchText = values.search?.value ?? '';
    setSearchText(newSearchText);
    updateQueryParams(pageBasePath, { search: newSearchText, sort, page: 0 }, 'replace');
  };

  return (
    <Div>
      <Headline size="md" margin={{ top: 'xlg' }} padding={{ top: 'md' }}>
        {t('usersAdmin.usersAdminList.title')}
      </Headline>

      <Gutter size={'lg'} />
      <Flex>
        <Button iconName="excel" iconOnRight={true} variant={'secondary'} onClick={() => history.push('/users/import')}>
          {t('usersAdmin.usersAdminList.importUsersButton')}
        </Button>
        <Gutter size="sm" />
        <Button
          iconName="add"
          iconOnRight={true}
          variant={'secondary'}
          onClick={() => setShowInviteUserModal(true)}
          loading={loadingInvoicingAccounts}
          hidden={!organization}
        >
          {t('usersAdmin.usersAdminList.inviteUserButton')}
        </Button>
      </Flex>

      <Gutter size={'lg'} />

      {searchText !== undefined && (
        <SearchAndFilter
          onChangeCallback={handleSearchChange}
          searchConfig={{
            useDebounceInTextSearch: 1000,
            searchInputOrSearchInputWithScopeProps: {
              placeholder: t('usersAdmin.usersAdminList.searchUsersPlaceholder'),
              title: t('usersAdmin.usersAdminList.searchUsersTitle'),
              forceUpdateValue: searchText,
            } as SearchWithoutScopeProps,
          }}
        />
      )}

      <GenericTable
        loading={loadingUsers ? 'short' : 'none'}
        noDataText={t('usersAdmin.usersAdminList.noUsersData')}
        errorText={usersFetchError ? tErrors('user.getAllUsersError') : undefined}
        inputData={pagedUsers?.contents}
        buttonConfig={buttonConfig}
        rowConfigs={rowConfigs}
      />
      {!!pagedUsers?.page_metadata.total_items && (
        <CustomPaginator
          activatePageInfoObject={pagedUsers?.page_metadata}
          onPageChange={change => {
            updateQueryParams(pageBasePath, { search: searchText!, sort, page: change.nextChosenPageNumber }, 'replace');
          }}
          paginatorStyleProps={{
            paginationProps: {},
            pageSelectorProps: { centered: true },
          }}
        />
      )}

      {organization && showInviteUserModal && (
        <InviteUserModal
          invoicingAccounts={invoicingAccounts || []}
          countries={COUNTRY_CALLING_CODES}
          onUserSave={inviteNewUser}
          onCloseModal={() => setShowInviteUserModal(false)}
          organization={organization}
          translations={translations}
        />
      )}
    </Div>
  );
};

export default UserAdminList;
